import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation } from 'react-router-dom'
import { getCurrentVariant } from '../../utils/siteVariant'

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID

function getGtag() {
  if (typeof window === 'undefined') return null

  window.dataLayer = window.dataLayer || []
  window.gtag =
    window.gtag ||
    function gtag() {
      window.dataLayer.push(arguments)
    }

  return window.gtag
}

function getVisitContext(location) {
  const params = new URLSearchParams(location.search)
  const hostname = window.location.hostname.toLowerCase()
  const siteVariant = getCurrentVariant() || 'general'
  const referrer = document.referrer.toLowerCase()
  const campaignSource = (params.get('utm_source') || '').toLowerCase()
  const campaignMedium = (params.get('utm_medium') || '').toLowerCase()
  const campaignName = (params.get('utm_campaign') || '').toLowerCase()
  const recruitingSignal = [
    referrer,
    campaignSource,
    campaignMedium,
    campaignName,
  ].join(' ')
  const hasRecruitingSignal =
    siteVariant !== 'general' ||
    location.pathname.startsWith('/cv') ||
    /(linkedin|recruit|talent|job|career|application|hiring)/.test(
      recruitingSignal,
    )

  return {
    site_hostname: hostname,
    site_variant: siteVariant,
    audience_hint: hasRecruitingSignal ? 'recruiting' : 'general',
  }
}

function GoogleAnalytics() {
  const location = useLocation()

  useEffect(() => {
    if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return

    const gtag = getGtag()
    const visitContext = getVisitContext(location)

    gtag('event', 'page_view', {
      page_path: `${location.pathname}${location.search}`,
      page_location: window.location.href,
      page_title: document.title,
      ...visitContext,
    })
  }, [location])

  useEffect(() => {
    if (!GA_MEASUREMENT_ID || typeof window === 'undefined') return undefined

    const gtag = getGtag()
    const visitContext = getVisitContext(location)
    const sessionKey = 'jp_human_interaction_recorded'
    let interactionRecorded = sessionStorage.getItem(sessionKey) === 'true'

    const recordRecruiterInterest = () => {
      if (visitContext.audience_hint !== 'recruiting') return

      const signal =
        visitContext.site_variant !== 'general'
          ? 'tailored_subdomain'
          : location.pathname.startsWith('/cv')
          ? 'cv_view'
          : 'recruiting_referral'
      const recruiterKey = `jp_recruiter_interest_${signal}_${visitContext.site_variant}`

      if (sessionStorage.getItem(recruiterKey) === 'true') return

      sessionStorage.setItem(recruiterKey, 'true')
      gtag('event', 'recruiter_interest', {
        signal,
        ...visitContext,
      })
    }

    const recordHumanInteraction = (event) => {
      if (interactionRecorded || !event.isTrusted) return

      interactionRecorded = true
      sessionStorage.setItem(sessionKey, 'true')
      gtag('set', 'user_properties', {
        visitor_type: 'likely_human',
      })
      gtag('event', 'human_interaction', {
        interaction_type: event.type,
        ...visitContext,
      })
      recordRecruiterInterest()

      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, recordHumanInteraction)
      })
    }

    const recordOutboundClick = (event) => {
      const link = event.target.closest && event.target.closest('a')
      if (!link || !link.href) return

      const destination = new URL(link.href, window.location.href)
      if (destination.protocol === 'mailto:') {
        gtag('event', 'contact_click', {
          contact_method: 'email',
          ...visitContext,
        })
        return
      }

      if (
        destination.protocol.startsWith('http') &&
        destination.hostname !== window.location.hostname
      ) {
        gtag('event', 'outbound_click', {
          link_domain: destination.hostname,
          link_path: destination.pathname,
          ...visitContext,
        })
      }
    }

    const interactionEvents = ['pointerdown', 'keydown', 'touchstart', 'scroll']
    if (interactionRecorded) {
      gtag('set', 'user_properties', {
        visitor_type: 'likely_human',
      })
      recordRecruiterInterest()
    } else {
      interactionEvents.forEach((eventName) => {
        window.addEventListener(eventName, recordHumanInteraction, {
          passive: true,
        })
      })
    }
    document.addEventListener('click', recordOutboundClick)

    return () => {
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, recordHumanInteraction)
      })
      document.removeEventListener('click', recordOutboundClick)
    }
  }, [location])

  if (!GA_MEASUREMENT_ID) return null

  return (
    <Helmet>
      <script>
        {`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          window.gtag = gtag;
          gtag('js', new Date());
          gtag('config', '${GA_MEASUREMENT_ID}', {
            send_page_view: false,
            cookie_domain: 'auto',
            cookie_flags: 'SameSite=Lax;Secure',
            allow_google_signals: false,
            allow_ad_personalization_signals: false
          });
        `}
      </script>
      <script
        async
        src={`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`}
      />
    </Helmet>
  )
}

export default GoogleAnalytics
